import notebookRequest from '@apis/notebook'
import {
  Box,
  Text,
  Flex,
  Button,
  Image,
  Center,
  useBreakpointValue,
  useDisclosure,
} from '@chakra-ui/react'
import {CommonColors} from '@utils/CommonStyles'
import {useEffect, useState} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'
import Folder from '@assets/svgs/learn/folder.svg'
import ArrowRight from '@assets/svgs/learn/arrow_right_3.svg'
import Loading from '@components/Loading'
import ArrowLeft from '@assets/svgs/learn/arrow_left.svg'
import NotDataImage from '@assets/svgs/no_data.svg'
import DetailSentence from './DetailSentence'
import DetailPhrase from './DetailPhrase'
import DetailModal from './DetailModal'
import {PATH} from '@utils/Const'

interface NotebookCardProps {
  type: 'SENTENCE' | 'PHRASE'
  isDisable: boolean
}

const NoteBookCard = ({type, isDisable}: NotebookCardProps) => {
  const {entry_point} = useLocation().state || {}
  const [folders, setFolders] = useState<any>()
  const [detailFolder, setDetailFolder] = useState<any>()
  const [currentFolder, setCurrentFolder] = useState<any>()
  const [currentKnowledge, setCurrentKnowledge] = useState<any>()
  const [detailKnowledge, setDetailKnowledge] = useState<any>()
  const [isLoadingFolders, setIsLoadingFolders] = useState<boolean>(false)
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false)
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(
    null,
  )
  const {isOpen, onOpen, onClose} = useDisclosure()
  const isMobile = useBreakpointValue({base: true, lg: false})

  useEffect(() => {
    const getFolders = async () => {
      setIsLoadingFolders(true)
      try {
        const params = {type: type}
        const res = await notebookRequest.getFolder(params)
        setFolders(res?.data?.data)
      } catch (error) {
      } finally {
        setIsLoadingFolders(false)
      }
    }

    getFolders()
  }, [])

  useEffect(() => {
    if (!currentFolder?.id) return

    const getDetailFolder = async () => {
      setIsLoadingFolders(true)
      try {
        const res = await notebookRequest.getDetailFolder(currentFolder.id)
        setDetailFolder(res?.data.data)
      } catch (error) {
      } finally {
        setIsLoadingFolders(false)
      }
    }

    getDetailFolder()
  }, [currentFolder])

  useEffect(() => {
    if (!currentKnowledge) return

    let params
    switch (type) {
      case 'SENTENCE':
        params = {
          sentencesBucketId: currentKnowledge.id,
          folderId: currentFolder.id || currentKnowledge.folder_id,
        }
        getDetailSentence(params)
        break
      case 'PHRASE':
        params = {
          phraseBucketId: currentKnowledge.id,
          folderId: currentFolder.id,
        }
        getDetailPhrases(params)
        break
      default:
        break
    }
  }, [currentKnowledge])

  const navigate = useNavigate()

  const handlePlayAudio = (url: string) => {
    if (!url) return

    if (currentAudio) {
      currentAudio.pause()
      currentAudio.currentTime = 0
    }

    const audio = new Audio(url)
    setCurrentAudio(audio)
    audio.play()
  }

  const getDetailPhrases = async (params: any) => {
    setIsLoadingDetail(true)
    try {
      const res = await notebookRequest.getDetailPhrases(params)
      setDetailKnowledge(res?.data.data)
    } catch (error) {
    } finally {
      setIsLoadingDetail(false)
    }
  }

  const getDetailSentence = async (params: any) => {
    setIsLoadingDetail(true)
    try {
      const res = await notebookRequest.getDetailSentence(params)
      setDetailKnowledge(res?.data.data)
    } catch (error) {
    } finally {
      setIsLoadingDetail(false)
    }
  }

  const handleBackFolder = () => {
    setCurrentFolder(null)
    setCurrentKnowledge(null)
    setDetailFolder(null)
    setDetailKnowledge(null)
  }

  const handleClickPractice = () => {
    if (type === 'SENTENCE') {
      navigate(PATH.LEARN_REVISE_SENTENCE_OVERVIEW, {
        state: {entry_point},
      })
    } else {
      navigate(PATH.LEARN_REVISE, {
        state: {entry_point},
      })
    }
  }

  const renderHeader = () => {
    return (
      <Flex justifyContent={'space-between'}>
        <Text fontSize={{base: '16px', md: '24px'}} fontWeight={600}>
          {type === 'SENTENCE' ? 'Mẫu câu đã lưu' : 'Cụm từ đã lưu'}
        </Text>
        <Button
          isDisabled={isDisable}
          fontSize={{base: '14px', md: '16px'}}
          bg={CommonColors.primary}
          color={'white'}
          px={{base: '20px', md: '30px'}}
          py={{base: '16px', md: '22px'}}
          _hover={{bg: CommonColors.primary}}
          onClick={handleClickPractice}>
          {type === 'SENTENCE' ? 'Ôn mẫu câu' : 'Ôn cụm từ'}
        </Button>
      </Flex>
    )
  }

  const renderBreadcrumb = () => {
    return detailFolder ? (
      <Flex mb={'16px'}>
        <Flex cursor={'pointer'} onClick={handleBackFolder}>
          <Image src={ArrowLeft} />
          <Text
            fontWeight={600}
            color={'#1F1F1F'}
            pr={'4px'}
            whiteSpace={'nowrap'}>
            Thư mục
          </Text>
        </Flex>
        <Text color={'#1F1F1F'} isTruncated>{`/ ${currentFolder?.name}`}</Text>
      </Flex>
    ) : (
      <Text color={'#1F1F1F'}>Thư mục</Text>
    )
  }

  const renderFolderList = () => {
    return (
      <Flex flexDirection={'column'} gap={'16px'} w={'100%'}>
        {detailFolder ? (
          detailFolder?.knowledge_buckets?.length > 0 ? (
            detailFolder?.knowledge_buckets.map((item: any, index: number) => (
              <Flex
                key={index}
                gap={'16px'}
                w={'100%'}
                justifyContent={'space-between'}
                p={'12px'}
                cursor={'pointer'}
                borderRadius={'8px'}
                border={`1px solid ${item?.id === currentKnowledge?.id ? '#8ED4FF' : 'transparent'}`}
                boxShadow={
                  item?.id === currentKnowledge?.id
                    ? '0px 4px 0px 0px var(--Dodger-Blue-300, #8ED4FF)'
                    : 'none'
                }
                onClick={() => {
                  setCurrentKnowledge(item)
                  onOpen()
                }}>
                <Flex alignItems={'center'} gap={'16px'} flex={1} minWidth={0}>
                  <Image src={Folder} />
                  <Text fontSize={'16px'} fontWeight={500} isTruncated>
                    {item.content}
                  </Text>
                </Flex>
                <Flex alignItems={'center'}>
                  <Image src={ArrowRight} flexShrink={0} />
                </Flex>
              </Flex>
            ))
          ) : (
            <Text>{`Không có ${type === 'SENTENCE' ? 'mẫu câu' : 'cụm từ'} nào`}</Text>
          )
        ) : folders?.folders?.length > 0 ? (
          folders?.folders?.map((folder: any, index: any) => {
            return (
              <Flex
                key={index}
                gap={'16px'}
                w={'100%'}
                justifyContent={'space-between'}
                p={'12px'}
                cursor={'pointer'}
                onClick={() => setCurrentFolder(folder)}>
                <Flex alignItems={'center'} gap={'16px'} flex={1} minWidth={0}>
                  <Image src={Folder} flexShrink={0} />
                  <Text fontSize={'16px'} fontWeight={500} isTruncated flex={1}>
                    {folder.name}
                  </Text>
                </Flex>
                <Flex alignItems={'center'} gap={'16px'} flexShrink={0}>
                  <Text fontSize={'16px'} fontWeight={500} color={'#85888E'}>
                    {folder.quantity}
                  </Text>
                  <Image src={ArrowRight} />
                </Flex>
              </Flex>
            )
          })
        ) : (
          <Text>{`Không có thư mục nào`}</Text>
        )}
      </Flex>
    )
  }

  const renderDetailContent = () => {
    if (isLoadingDetail) return <Loading />

    if (!detailKnowledge) {
      return (
        <Center
          h="100%"
          display={{base: 'none', lg: 'flex'}}
          flexDirection="column">
          <Image src={NotDataImage} w={{base: '100px', md: '160px'}} />
          <Text fontSize="16px" color="#8C8C8C">
            Chọn mục bên trái để xem chi tiết
          </Text>
        </Center>
      )
    }

    return type === 'SENTENCE' ? (
      <DetailSentence
        detailKnowledge={detailKnowledge}
        handlePlayAudio={handlePlayAudio}
      />
    ) : (
      <DetailPhrase
        detailKnowledge={detailKnowledge}
        handlePlayAudio={handlePlayAudio}
      />
    )
  }

  return (
    <Box p={{base: '16px', md: '24px'}} bg={'white'} borderRadius={'16px'}>
      {renderHeader()}
      <Box h={'1px'} bg={'#EFEFEF'} my={{base: '16px', md: '24px'}} />
      <Flex h={'400px'} gap={{base: 0, lg: '24px'}}>
        <Box
          w={{base: '100%', lg: '350px'}}
          h={'100%'}
          overflow={'auto'}
          position={'relative'}>
          {isLoadingFolders && <Loading />}
          {renderBreadcrumb()}
          {renderFolderList()}
        </Box>
        <Box h={'100%'} overflow={'auto'} flex={1} position={'relative'}>
          {renderDetailContent()}
        </Box>
      </Flex>
      <DetailModal
        isOpen={Boolean(isOpen && isMobile)}
        onClose={onClose}
        detailKnowledge={detailKnowledge}
        handlePlayAudio={handlePlayAudio}
        type={type}
      />
    </Box>
  )
}

export default NoteBookCard
